import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Paperclip, X, File as FileIcon, CircleNotch } from "@phosphor-icons/react";

export function humanSize(b) {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} Ko`;
  return `${(b / 1024 / 1024).toFixed(1)} Mo`;
}

export function AttachmentUploader({ value = [], onChange, testid = "attachments", compact = false }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      setUploading(true);
      try {
        const { data } = await api.post("/crm/inbox/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange([...(value || []), { file_id: data.file_id, filename: data.filename, size: data.size }]);
      } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
      finally { setUploading(false); }
    }
  };

  const remove = (fid) => onChange((value || []).filter((a) => a.file_id !== fid));

  return (
    <div data-testid={testid}>
      <input ref={inputRef} type="file" multiple hidden onChange={onFiles} data-testid={`${testid}-input`}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.png,.jpg,.jpeg,.gif,.webp,.heic,.zip" />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
        data-testid={`${testid}-btn`}
        className="flex items-center gap-1.5 text-xs text-muted hover:text-fg border border-border rounded-md px-2.5 py-1.5 hover:bg-surface-2 disabled:opacity-50">
        {uploading ? <CircleNotch size={13} className="animate-spin" /> : <Paperclip size={13} />}
        {uploading ? "Envoi…" : (compact ? "Joindre" : "Joindre un document")}
      </button>
      {(value || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testid}-list`}>
          {value.map((a) => (
            <span key={a.file_id} data-testid={`${testid}-chip-${a.file_id}`}
              className="flex items-center gap-1.5 text-[11px] bg-surface-2 border border-border rounded-full pl-2 pr-1 py-1 max-w-[220px]">
              <FileIcon size={12} className="text-accent shrink-0" />
              <span className="truncate text-fg/90">{a.filename}</span>
              {a.size != null && <span className="text-muted font-mono">{humanSize(a.size)}</span>}
              <button type="button" onClick={() => remove(a.file_id)} data-testid={`${testid}-remove-${a.file_id}`}
                className="shrink-0 w-4 h-4 rounded-full hover:bg-destructive/20 text-muted hover:text-destructive flex items-center justify-center">
                <X size={10} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
